import React, { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/Button';
import { Input, Select } from '../components/Input';
import { requestMagicLink, verifyMagicLink } from '../services/auth';

export const AuthPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSignup = location.pathname === '/signup';
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Student');
  const [newsletter, setNewsletter] = useState(false);
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      return;
    }
    setError(null);
    setIsSubmitting(true);
    verifyMagicLink(token)
      .then((response) => {
        if (!response.user?.id) {
          throw new Error('Unable to verify magic link.');
        }
        navigate('/app');
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Unable to verify magic link.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [token, navigate]);

  useEffect(() => {
    setStep('form');
    setError(null);
  }, [location.pathname]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await requestMagicLink({
        email,
        role: isSignup ? role : undefined,
        newsletterOptIn: isSignup ? newsletter : undefined,
      });
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send magic link.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-surface p-8 rounded-2xl shadow-lg border border-border">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-text">{isSignup ? 'Create an account' : 'Continue with email'}</h2>
          <p className="text-muted mt-2">
            {isSignup
              ? 'We will send you a magic link to finish your signup.'
              : 'Enter your email to get a magic link. We will create an account if you are new.'}
          </p>
        </div>

        {step === 'form' ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input 
                label="Email address" 
                type="email" 
                placeholder="you@example.com" 
                required 
                value={email}
                onChange={e => setEmail(e.target.value)}
            />
            
            {isSignup && (
                <>
                    <Select label="I am a..." value={role} onChange={e => setRole(e.target.value)}>
                        <option value="Student">Student</option>
                        <option value="Employee">Employee</option>
                        <option value="Manager">Manager</option>
                        <option value="Founder">Founder</option>
                        <option value="Freelancer">Freelancer</option>
                        <option value="Other">Other</option>
                    </Select>
                    
                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                id="newsletter"
                                type="checkbox"
                                className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                                checked={newsletter}
                                onChange={e => setNewsletter(e.target.checked)}
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="newsletter" className="font-medium text-text">Subscribe to updates</label>
                            <p className="text-muted">Get tips on productivity and feature announcements.</p>
                        </div>
                    </div>
                </>
            )}

            {error && <p className="text-sm text-no">{error}</p>}
            <Button type="submit" size="lg" fullWidth disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : isSignup ? 'Create account' : 'Continue with email'}
            </Button>
            
            <p className="text-xs text-center text-muted mt-4">
               Protected by simple rate limiting.
            </p>
          </form>
        ) : (
           <div className="text-center space-y-6 animate-in fade-in zoom-in duration-300">
               <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                   <span className="text-2xl">✉️</span>
               </div>
               <div>
                   <h3 className="text-lg font-medium text-text">Check your inbox</h3>
                   <p className="text-muted mt-2">We sent a magic link to <strong>{email}</strong>. It expires in 15 minutes.</p>
                   <p className="text-xs text-muted mt-2">First time here? The link will create your account.</p>
               </div>
               {error && <p className="text-sm text-no">{error}</p>}
               <button onClick={() => setStep('form')} className="text-sm text-primary hover:underline">
                   Try with a different email
               </button>
           </div>
        )}

        <div className="mt-6 text-center text-sm">
          {isSignup ? (
             <p className="text-muted">Already have an account? <Link to="/login" className="text-primary hover:underline">Log in</Link></p>
          ) : (
             <p className="text-muted">Don't have an account? <Link to="/signup" className="text-primary hover:underline">Sign up</Link></p>
          )}
        </div>
      </div>
    </div>
  );
};
